import React, { useEffect } from "react";
import { connect } from "react-redux";
import {
  Container,
  Header,
  Segment,
  Table,
  Button,
  Icon,
  Message,
} from "semantic-ui-react";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import PropTypes from "prop-types";
import {
  getSessions,
  closeSession,
  closeAllSessions,
} from "../../../app/store/actions/sessionActions";

const mapState = (state) => ({
  currentUser: state.auth.currentUser,
  sessions: state.sessionReducer?.sessions || [],
  loadingSessions: state.sessionReducer?.loadingSessions || false,
  loadingCloseSession: state.sessionReducer?.loadingCloseSession || false,
  loadingCloseAllSessions:
    state.sessionReducer?.loadingCloseAllSessions || false,
});

const actions = {
  getSessions,
  closeSession,
  closeAllSessions,
};

const SessionsPage = ({
  currentUser,
  sessions = [], // valor por defecto
  loadingSessions = false, // valor por defecto
  loadingCloseSession = false, // valor por defecto
  loadingCloseAllSessions = false, // valor por defecto
  getSessions,
  closeSession,
  closeAllSessions,
}) => {
  useEffect(() => {
    if (currentUser) {
      getSessions();
    }
  }, [getSessions, currentUser]);

  const handleCloseSession = (codSesion) => {
    closeSession({ id: codSesion });
  };

  const handleCloseAllSessions = () => {
    closeAllSessions({});
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString("es-PE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getIconForBrowser = (navegador) => {
    if (!navegador) return "laptop";
    const browser = navegador.toLowerCase();
    if (browser.includes("chrome")) return "chrome";
    if (browser.includes("firefox")) return "firefox";
    if (browser.includes("safari")) return "safari";
    if (browser.includes("edge")) return "edge";
    if (browser.includes("opera")) return "opera";
    return "laptop";
  };

  if (!currentUser) {
    return <LoadingComponent content="Cargando usuario..." />;
  }

  if (loadingSessions) {
    return <LoadingComponent content="Cargando sesiones..." />;
  }

  // Asegurar que sessions sea un array
  const sessionsList = sessions || [];
  const activeSessions = sessionsList.filter((session) => session.activa);
  const hasMultipleActiveSessions = activeSessions.length > 1;

  return (
    <Container className="containerPrincipal">
      <Header as="h1" color="blue" textAlign="center">
        <Icon name="laptop" />
        Mis Sesiones Activas
      </Header>

      <Segment>
        <Header as="h3">
          <Icon name="user" />
          {`Usuario: ${currentUser.primerNombre} ${currentUser.apellidoPaterno} ${currentUser.apellidoMaterno}`}
        </Header>

        <Message info>
          <Message.Header>Información de Seguridad</Message.Header>
          <p>
            Aquí puedes ver y administrar todas las sesiones activas en tu
            cuenta. Si detectas alguna sesión sospechosa, puedes cerrarla
            inmediatamente.
          </p>
        </Message>

        {hasMultipleActiveSessions && (
          <Button
            color="red"
            onClick={handleCloseAllSessions}
            loading={loadingCloseAllSessions}
            disabled={loadingCloseAllSessions}
            style={{ marginBottom: "1em" }}
          >
            <Icon name="close" />
            Cerrar todas las otras sesiones
          </Button>
        )}

        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Navegador</Table.HeaderCell>
              <Table.HeaderCell>Fecha de Inicio</Table.HeaderCell>
              <Table.HeaderCell>Último Acceso</Table.HeaderCell>
              <Table.HeaderCell>Dirección IP</Table.HeaderCell>
              <Table.HeaderCell>Estado</Table.HeaderCell>
              <Table.HeaderCell>Acciones</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {sessionsList.map((session) => (
              <Table.Row key={session.codSesion}>
                <Table.Cell>
                  <Icon name={getIconForBrowser(session.navegador)} />
                  {session.navegador || "Navegador desconocido"}
                </Table.Cell>
                <Table.Cell>{formatDate(session.fechaInicio)}</Table.Cell>
                <Table.Cell>{formatDate(session.ultimoAcceso)}</Table.Cell>
                <Table.Cell>{session.direccionIp || "N/A"}</Table.Cell>
                <Table.Cell>
                  {session.activa ? (
                    <span style={{ color: "green" }}>
                      <Icon name="circle" />
                      Activa
                    </span>
                  ) : (
                    <span style={{ color: "gray" }}>
                      <Icon name="circle outline" />
                      Inactiva
                    </span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {session.activa ? (
                    <Button
                      size="small"
                      color="red"
                      onClick={() => handleCloseSession(session.codSesion)}
                      loading={loadingCloseSession}
                      disabled={loadingCloseSession}
                    >
                      <Icon name="close" />
                      Cerrar Sesión
                    </Button>
                  ) : (
                    <span style={{ color: "gray" }}>Sesión cerrada</span>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {sessionsList.length === 0 && (
          <Message warning>
            <Message.Header>Sin sesiones</Message.Header>
            <p>No hay sesiones para mostrar.</p>
          </Message>
        )}
      </Segment>
    </Container>
  );
};

SessionsPage.propTypes = {
  currentUser: PropTypes.object,
  sessions: PropTypes.array,
  loadingSessions: PropTypes.bool,
  loadingCloseSession: PropTypes.bool,
  loadingCloseAllSessions: PropTypes.bool,
  getSessions: PropTypes.func.isRequired,
  closeSession: PropTypes.func.isRequired,
  closeAllSessions: PropTypes.func.isRequired,
};

export default connect(mapState, actions)(SessionsPage);
